import { useRef, useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import throttle from "lodash.throttle"

import { nav as nav_cn, hidden } from "../CardCarousel/CardCarousel.module.css"

const Carousel = styled.div`
  display: block;
  position relative;
`

const CarouselContainer = styled.div`
  display: flex;
  overflow-x: scroll;
  align-items: stretch;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  padding-top: 5px;

  ${(props) =>
    props.nav === "side" &&
    `
  margin: 0 -16px;
  }`}

  scrollbar-color: transparent transparent; /* thumb and track color */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border: none;
  }

  ${(props) =>
    props.nav === "side" &&
    `
  @media (min-width: 992px) {
    margin-left: 92px;
    margin-right: 92px;
  }`}
`

const Wrapper = styled.div`
  padding-right: 24px;
  display: flex;
  flex-shrink: 0;
  position: relative;
  scroll-snap-align: end;
  flex-basis: calc(${(props) => props.itemWidth.sm} + ${(props) => props.gap});

  @media (min-width: 768px) {
    flex-basis: calc(${(props) => props.itemWidth.md} + ${(props) => props.gap});
  }

  @media (min-width: 992px) {
    flex-basis: calc(${(props) => props.itemWidth.lg} + ${(props) => props.gap});
  }

  @media (min-width: 1200px) {
    flex-basis: calc(${(props) => props.itemWidth.xl} + ${(props) => props.gap});
  }

  ${(props) =>
    props.nav === "bottom" &&
    `
    &:first-child {
      margin-left: calc(50vw - 570px);
    }
  `}

  &:first-child {
    margin-inline-start: ${(props) => props.gap};
  }
`

const Nav = styled.nav`
  display: none;

  @media (min-width: 992px) {
    display: block;
  }

  #previous {
    position: absolute;
    top: calc(50% - 23px);
    left: 0;
  }

  #next {
    position: absolute;
    top: calc(50% - 23px);
    right: 0;
  }
`

const CircleButton = styled.button`
  mask: url("/g-static/icons/circlearrow.svg");
  mask-size: cover;
  -webkit-mask: url("/g-static/icons/circlearrow.svg");
  -webkit-mask-size: cover;
  background: var(--grey-90);
  width: 51px;
  height: 51px;
  border: none;
  cursor: pointer;
  outline: none;
  ${(props) => props.id === "next" && `transform: rotate(180deg);`}

  &:hover {
    background: var(--red);
  }

  &:focus {
    background: var(--red);
  }

  &:disabled {
    background: var(--grey-40);
  }
`

const Scroller = ({ itemWidth, itemList, nav, gap }) => {
  const [hideNav, setHidden] = useState(false)
  const [scrollPosition, setScrollPosition] = useState("left")
  const scrollContainer = useRef(null)
  const scroller = useRef(null)
  const cardRef = useRef(null)

  const items = itemList.map((item) => {
    return (
      <Wrapper itemWidth={itemWidth} gap={gap} nav={nav} ref={cardRef} key={item.key}>
        {item}
      </Wrapper>
    )
  })

  const scrollHandler = (evt) => {
    throttle(() => {
      const { scrollLeft, clientWidth, scrollWidth } = evt.target

      if (scrollLeft === 0) {
        setScrollPosition("left")
      } else if (scrollLeft + clientWidth === scrollWidth) {
        setScrollPosition("right")
      } else {
        setScrollPosition("middle")
      }
    }, 200)()
  }

  useEffect(() => {
    if (scroller.current.scrollWidth <= scrollContainer.current.scrollWidth) {
      setHidden(true)
    }
  }, [])

  const handleScroll = (target) => {
    const cardWidth = cardRef.current.offsetWidth

    const scrollByMap = {
      previous: -cardWidth,
      next: cardWidth,
    }

    scroller.current.scrollBy({
      left: scrollByMap[target.id],
      behavior: "smooth",
    })
  }

  return (
    <Carousel ref={scrollContainer}>
      <CarouselContainer ref={scroller} itemWidth={itemWidth} nav={nav} onScroll={scrollHandler}>
        {items}
      </CarouselContainer>

      {nav === "side" && (
        <Nav>
          <CircleButton
            aria-label="previous"
            id="previous"
            disabled={scrollPosition === "left"}
            onClick={(e) => handleScroll(e.target)}
          />
          <CircleButton
            aria-label="next"
            id="next"
            disabled={scrollPosition === "right"}
            onClick={(e) => handleScroll(e.target)}
          />
        </Nav>
      )}

      {nav === "bottom" && (
        <nav className={`${nav_cn} ${hideNav ? hidden : ""}`}>
          <button
            aria-label="previous"
            id="previous"
            className="circle-button circle-button--prev"
            onClick={(e) => handleScroll(e.target)}
          ></button>
          <button
            aria-label="next"
            id="next"
            className="circle-button circle-button--next"
            onClick={(e) => handleScroll(e.target)}
          ></button>
        </nav>
      )}
    </Carousel>
  )
}

Scroller.propTypes = {
  itemList: PropTypes.arrayOf(PropTypes.node),
  itemWidth: PropTypes.shape({
    sm: PropTypes.string,
    md: PropTypes.string,
    lg: PropTypes.string,
    xl: PropTypes.string,
  }),
  nav: PropTypes.string,
  gap: PropTypes.string,
}

Scroller.defaultProps = {
  itemWidth: {
    sm: "300px",
    md: "345px",
    lg: "360px",
    xl: "360px",
  },
  nav: "bottom",
  gap: "30px",
}

export default Scroller
